import { useNavigate } from "react-router-dom";
import { Home, MapPin, Building2, Armchair, Briefcase, Truck, Package, Clock, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";

const SERVICES = [
  { icon: Home, title: "House Moving", desc: "Full house moves across Melbourne — loading to final placement.", to: "/#quote", img: "/images/movers-sofa.png" },
  { icon: MapPin, title: "Local Removalists", desc: "Local moves across Greater Melbourne, done safely and efficiently.", to: "/#quote", img: "/images/truck-medium.png" },
  { icon: Building2, title: "Apartment & Unit Moving", desc: "Stairs, lifts and tight access — we plan around your property.", to: "/#quote", img: "/images/boxes-branded.png" },
  { icon: Armchair, title: "Furniture Removal", desc: "Sofas, beds, appliances and single-item transfers.", to: "/#quote", img: "/images/hero-branded.png" },
  { icon: Briefcase, title: "Office Moving & Relocation", desc: "Business relocations with less disruption to your work.", to: "/#quote", img: "/images/melbourne-skyline.png" },
  { icon: Truck, title: "Interstate Removalists", desc: "Melbourne to Sydney, Brisbane, Adelaide and beyond.", to: "/#quote", img: "/images/truck-road.png" },
  { icon: Package, title: "Packing Services", desc: "Professional packing with quality materials and care.", to: "/#quote", img: "/images/packing-closeup.png" },
  { icon: Clock, title: "Same-Day Moving", desc: "Urgent move? Call us and we'll check today's availability.", to: "/#quote", img: "/images/truck-large.png", highlight: true },
];

const Card = ({ s, delay }) => {
  const navigate = useNavigate();
  const Icon = s.icon;
  return (
    <Reveal delay={delay}>
      <button
        onClick={() => navigate(s.to)}
        className={`group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border p-6 text-left transition-all duration-300 hover:-translate-y-1 ${
          s.highlight
            ? "border-blaze text-white shadow-md hover:shadow-xl"
            : "border-slate-200 text-slate-900 shadow-sm hover:border-blue-300 hover:shadow-lg"
        }`}
        data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      >
        <img src={s.img} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <span className={`absolute inset-0 transition-colors duration-300 ${s.highlight ? "bg-blaze/70 group-hover:bg-blaze/60" : "bg-white/70 group-hover:bg-white/55"}`} />
        <span className="relative z-10 flex h-full flex-col">
          <span className={`flex h-12 w-12 items-center justify-center rounded-xl shadow-sm ${s.highlight ? "bg-white/15 backdrop-blur-sm" : "bg-white/90"}`}>
            <Icon className={`h-6 w-6 ${s.highlight ? "text-white" : "text-blaze"}`} strokeWidth={1.8} />
          </span>
          <span className="mt-5 font-display text-lg font-semibold tracking-tight">{s.title}</span>
          <span className={`mt-2 text-sm leading-relaxed ${s.highlight ? "text-white/90" : "text-slate-700"}`}>{s.desc}</span>
          <span className={`mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${s.highlight ? "text-white" : "text-blaze"}`}>
            Get a Quote
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </span>
      </button>
    </Reveal>
  );
};

export const Services = () => (
  <section id="services" className="border-y border-slate-200 bg-white/75" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 lg:px-10 lg:py-36">
      <Reveal>
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Our Services</p>
        <h2 className="font-display text-4xl font-semibold uppercase leading-[0.95] tracking-tighter [word-spacing:0.14em] sm:text-5xl lg:text-6xl" data-testid="services-heading">
          Melbourne <span className="text-blaze">removalists</span><br />
          you <span className="text-blaze">can</span> <span className="text-red-600">rely</span> on
        </h2>
        <div className="mt-6 max-w-3xl space-y-4 text-base leading-relaxed text-slate-600" data-testid="services-intro">
          <p>
            Moving house can be stressful. Finding a moving company that turns up on time, handles your
            belongings carefully and gives you clear pricing shouldn&rsquo;t be.
          </p>
          <p>
            At MoversNearby, we provide professional moving services across Melbourne for homes, apartments,
            offices and individual furniture moves. From your first quote to the final item being unloaded,
            our goal is simple: make your move easier, safer and more straightforward.
          </p>
          <p>
            Whether you&rsquo;ve searched for removalists Melbourne, Melbourne removalists, movers Melbourne
            or removalists near me, our team is ready to help with your move.
          </p>
        </div>
      </Reveal>

      <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <Card key={s.title} s={s} delay={i * 0.06} />
        ))}
      </div>
    </div>
  </section>
);
